/* /assets/section-product-redesign.js
 * PDP redesign behavior:
 *   - dynamic "Format · X" and "Quantity · X" labels
 *   - "N items total" helper using variant.metafields.linna.unit_count
 *   - free-shipping banner that updates on theme:cart:change
 *   - sticky mobile CTA via IntersectionObserver
 *   - opens cart drawer after add-to-cart (replacing the default popdown)
 *   - +/- button aria-disabled at min/max
 */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function moneyFormat(cents) {
    if (cents == null || isNaN(cents)) return '';
    var dollars = (cents / 100).toFixed(2);
    return '$' + dollars;
  }

  // Many product descriptions list features as manually-typed bullet
  // paragraphs ("<p>• ...</p>") rather than a semantic <ul>. Group consecutive
  // bullet paragraphs into a real <ul> so the boxed-list CSS can style them.
  function formatDescriptionBullets(scope) {
    var blocks = scope.querySelectorAll('.product__block__description');
    Array.prototype.forEach.call(blocks, function (desc) {
      var paras = Array.prototype.slice.call(desc.querySelectorAll(':scope > p, :scope > div > p'));
      var isBullet = function (p) {
        return /^\s*[•‣◦]/.test((p.textContent || ''));
      };
      var i = 0;
      while (i < paras.length) {
        if (!isBullet(paras[i])) { i++; continue; }
        var group = [];
        var j = i;
        while (j < paras.length && isBullet(paras[j])) { group.push(paras[j]); j++; }
        var ul = document.createElement('ul');
        group.forEach(function (gp) {
          var li = document.createElement('li');
          li.innerHTML = gp.innerHTML.replace(/^\s*[•‣◦]\s*/, '');
          ul.appendChild(li);
        });
        group[0].parentNode.insertBefore(ul, group[0]);
        group.forEach(function (gp) { gp.parentNode.removeChild(gp); });
        i = j;
      }
    });
  }

  // Collapse empty paragraphs/headings (e.g. "<p><br></p>") and stray layout
  // line breaks in the description so they don't leave blank gaps.
  function collapseEmptyDescription(scope) {
    var blocks = scope.querySelectorAll('.product__block__description');
    Array.prototype.forEach.call(blocks, function (desc) {
      Array.prototype.forEach.call(
        desc.querySelectorAll('p, h1, h2, h3, h4, h5, h6'),
        function (el) {
          var hasText = (el.textContent || '').trim().length > 0;
          var hasMedia = el.querySelector('img, iframe, video, picture, svg');
          if (!hasText && !hasMedia) el.style.display = 'none';
        }
      );
      // Stray <br> outside any paragraph/list/heading are decorative spacers.
      Array.prototype.forEach.call(desc.querySelectorAll('br'), function (br) {
        if (!br.closest('p, li, h1, h2, h3, h4, h5, h6')) br.style.display = 'none';
      });
    });
  }

  // When the Klaviyo widget shows reviews from OTHER products (the store-wide
  // "all reviews" view, which renders a product card per review), add a section
  // heading above it. Klaviyo renders async, so observe until cards appear.
  function injectReviewsHeading(config) {
    var container = document.querySelector('#klaviyo-reviews-all') ||
      document.querySelector('[id^="klaviyo-reviews"]');
    if (!container) return;

    var rh = (config && config.reviewsHeading) || {};
    var title = rh.title || 'What customers are saying';
    var subtitle = rh.subtitle || 'Real reviews from verified buyers across the LINNA collection.';

    function tryInject() {
      if (document.querySelector('.reviews-section-heading')) return true;
      // Product cards only render when showing cross-product (store-wide) reviews.
      if (!container.querySelector('.kl_reviews__review__product_card')) return false;
      var heading = document.createElement('div');
      heading.className = 'reviews-section-heading';
      var h2 = document.createElement('h2');
      h2.textContent = title;
      var p = document.createElement('p');
      p.textContent = subtitle;
      heading.appendChild(h2);
      heading.appendChild(p);
      container.parentNode.insertBefore(heading, container);
      return true;
    }

    if (tryInject()) return;
    if (!('MutationObserver' in window)) return;
    var obs = new MutationObserver(function () {
      if (tryInject()) obs.disconnect();
    });
    obs.observe(container, { childList: true, subtree: true });
  }

  // Overlay prev/next arrows on the main product image. The theme's Media
  // class (theme.js) drives the Flickity slideshow and listens for a
  // `theme:image:change` event on the slideshow element, selecting the slide
  // whose media id matches. We reuse that public event so we don't depend on
  // the (non-exposed) Flickity instance and stay in sync with drag + thumbs.
  function initMediaArrows(root) {
    var slideshow = root.querySelector('[data-product-slideshow]');
    var prevBtn = root.querySelector('[data-redesign-media-prev]');
    var nextBtn = root.querySelector('[data-redesign-media-next]');
    if (!slideshow || (!prevBtn && !nextBtn)) return;

    function getSlides() {
      return Array.prototype.slice.call(slideshow.querySelectorAll('[data-media-slide]'));
    }

    // Flickity marks the visible cell with `is-selected`; fall back to 0.
    function currentIndex(slides) {
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].classList.contains('is-selected')) return i;
      }
      return 0;
    }

    function step(delta) {
      var slides = getSlides();
      if (slides.length < 2) return;
      var target = (currentIndex(slides) + delta + slides.length) % slides.length;
      var id = slides[target].getAttribute('data-media-id');
      if (!id) return;
      slideshow.dispatchEvent(new CustomEvent('theme:image:change', { detail: { id: id } }));
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { step(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { step(1); });
  }

  ready(function () {
    var root = document.querySelector('.product--redesign');
    if (!root) return;

    formatDescriptionBullets(root);
    collapseEmptyDescription(root);
    initMediaArrows(root);

    var configEl = root.querySelector('[data-product-redesign-config]');
    var config = {};
    try {
      if (configEl) config = JSON.parse(configEl.textContent);
    } catch (e) {
      config = {};
    }

    injectReviewsHeading(config);

    // When a review's product-card image fails to load, the linked product is
    // likely gone (would 404) — hide the whole card. error doesn't bubble, so
    // listen in the capture phase; covers Klaviyo's async-loaded thumbnails.
    document.addEventListener('error', function (e) {
      var t = e.target;
      if (t && t.tagName === 'IMG' && t.closest) {
        var card = t.closest('.kl_reviews__review__product_card');
        // important — beats Klaviyo's display:flex on the card.
        if (card) card.style.setProperty('display', 'none', 'important');
      }
    }, true);

    var variantUnits = config.variantUnits || {};
    var freeShippingThreshold = parseInt(config.freeShippingThreshold, 10) || 0;

    var formatLabel = root.querySelector('[data-redesign-format-label]');
    var quantityLabel = root.querySelector('[data-redesign-quantity-label]');
    var helperEl = root.querySelector('[data-redesign-quantity-helper]');
    var qtyInput = root.querySelector('[data-quantity-input]');
    var minusBtn = root.querySelector('[data-decrease-quantity]');
    var plusBtn = root.querySelector('[data-increase-quantity]');
    var freeShipEl = root.querySelector('[data-redesign-free-shipping]');
    var addToCartBtn = root.querySelector('[data-add-to-cart]');

    function getCurrentVariant() {
      var idInput = root.querySelector('input[name="id"]');
      if (!idInput) return null;
      var id = idInput.value;
      var jsonEl = root.querySelector('[data-product-json]');
      if (!jsonEl) return null;
      try {
        var product = JSON.parse(jsonEl.textContent);
        for (var i = 0; i < product.variants.length; i++) {
          if (String(product.variants[i].id) === String(id)) return product.variants[i];
        }
      } catch (e) {}
      return null;
    }

    function getUnitCount(variant) {
      if (!variant) return 1;
      var v = variantUnits[String(variant.id)];
      var n = parseInt(v, 10);
      return n > 0 ? n : 1;
    }

    function updateLabels() {
      var variant = getCurrentVariant();
      var qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
      var unitCount = getUnitCount(variant);
      var totalUnits = qty * unitCount;

      if (formatLabel && variant) {
        // Variant title — Shopify joins option values with " / "
        formatLabel.textContent = variant.title.replace(/\s*\/\s*/g, ' · ');
      }

      // Keep the noun generic across the whole catalog — deriving it from the
      // variant title breaks on options like "Gift Wrapping", and a fixed
      // "card" noun is wrong for non-card products.
      var singular = config.unitNounSingular || 'item';
      var plural = config.unitNounPlural || 'items';

      if (quantityLabel) {
        quantityLabel.textContent = qty + ' ' + (qty === 1 ? singular : plural);
      }

      if (helperEl) {
        var totalNoun = totalUnits === 1 ? singular : plural;
        helperEl.innerHTML = '<strong>' + totalUnits + ' ' + totalNoun + ' total</strong>';
      }

      // +/- aria state
      if (minusBtn) {
        minusBtn.setAttribute('aria-disabled', qty <= 1 ? 'true' : 'false');
      }
      if (plusBtn) {
        var max = parseInt(qtyInput && qtyInput.getAttribute('max'), 10) || 99;
        plusBtn.setAttribute('aria-disabled', qty >= max ? 'true' : 'false');
      }
    }

    function updateFreeShipping(cart) {
      if (!freeShipEl || !freeShippingThreshold) return;
      var subtotal = cart && typeof cart.total_price === 'number' ? cart.total_price : 0;
      var diff = freeShippingThreshold - subtotal;
      var msgEl = freeShipEl.querySelector('[data-redesign-free-shipping-msg]');
      if (!msgEl) return;
      if (diff <= 0) {
        freeShipEl.setAttribute('data-state', 'met');
        msgEl.innerHTML = '<strong>You’ve unlocked free shipping</strong>';
      } else {
        freeShipEl.setAttribute('data-state', 'pending');
        msgEl.innerHTML = 'Add <strong>' + moneyFormat(diff) + ' more</strong> for free shipping';
      }
    }

    // Wire events
    document.addEventListener('theme:variant:change', updateLabels);
    if (qtyInput) qtyInput.addEventListener('input', updateLabels);
    if (qtyInput) qtyInput.addEventListener('change', updateLabels);
    if (minusBtn) minusBtn.addEventListener('click', function () { setTimeout(updateLabels, 0); });
    if (plusBtn) plusBtn.addEventListener('click', function () { setTimeout(updateLabels, 0); });

    document.addEventListener('theme:cart:change', function (e) {
      if (e && e.detail && e.detail.cart) updateFreeShipping(e.detail.cart);
    });

    // Initial state — also try fetching the cart for the free-shipping banner
    updateLabels();
    if (freeShipEl && window.fetch) {
      var cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
      fetch(cartUrl + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) { if (cart) updateFreeShipping(cart); })
        .catch(function () {});
    }

    // === Cart drawer open after Add to Cart =========================
    // Default theme behavior fires `openPopdown` on success unless
    // window.theme.state.cartOpen is already true. We flip that flag on
    // click so the popdown is suppressed, then explicitly open the drawer
    // when cart:change fires (the drawer's own listener will then load
    // the cart contents).
    var cartDrawer = document.querySelector('[data-drawer="drawer-cart"]');
    if (config.openCartDrawer && addToCartBtn && cartDrawer) {
      var pendingOpen = false;
      addToCartBtn.addEventListener('click', function () {
        pendingOpen = true;
        if (window.theme && window.theme.state) {
          window.theme.state.cartOpen = true;
        }
      });
      document.addEventListener('theme:cart:change', function () {
        if (!pendingOpen) return;
        pendingOpen = false;
        cartDrawer.dispatchEvent(new CustomEvent('theme:drawer:open', { bubbles: true }));
      });
    }

    // === Sticky mobile CTA ==========================================
    var sticky = document.querySelector('[data-product-sticky-cta]');
    if (sticky && addToCartBtn && 'IntersectionObserver' in window) {
      var stickyBtn = sticky.querySelector('[data-product-sticky-cta-button]');
      var stickyPriceEl = sticky.querySelector('[data-product-sticky-cta-price]');

      function syncSticky() {
        var variant = getCurrentVariant();
        if (variant && stickyPriceEl) {
          var qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
          stickyPriceEl.textContent = moneyFormat(variant.price * qty);
        }
        if (stickyBtn) {
          var disabled = !variant || variant.available === false;
          stickyBtn.disabled = disabled;
          stickyBtn.textContent = disabled ?
            (config.soldOutLabel || 'Sold out') :
            (config.addToCartLabel || 'Add to cart');
        }
      }

      // Clicking the sticky button forwards to the inline Add to Cart so the
      // existing Alpine product form handles submission.
      if (stickyBtn) {
        stickyBtn.addEventListener('click', function (e) {
          e.preventDefault();
          if (addToCartBtn.disabled) return;
          addToCartBtn.click();
        });
      }

      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          // Show sticky bar when inline CTA leaves the viewport
          var visible = entry.isIntersecting;
          sticky.setAttribute('data-active', visible ? 'false' : 'true');
        });
      }, { rootMargin: '0px', threshold: 0.01 });

      observer.observe(addToCartBtn);

      document.addEventListener('theme:variant:change', syncSticky);
      if (qtyInput) qtyInput.addEventListener('input', syncSticky);
      syncSticky();
    }
  });
})();
